import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { alpha, Box, Typography, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useTranslation } from 'react-i18next';
import { User, Warning2, Cake } from 'iconsax-react';
import { useParams } from 'react-router-dom';
import { GoogleMap, Marker, OverlayViewF, OverlayView, useJsApiLoader } from '@react-google-maps/api';
import AvatarInfoCard from '@ui/components/common/AvatarInfoCard';
import DonutChart from '@ui/components/DonutChart/DonutChart';
import CapacityVsRegisteredBarChart from './CapacityVsRegisteredBarChart';
import RegistrationTrendChart from './RegistrationTrendChart';
import PeopleCard from '@ui/pages/People/components/People-Card';
import ButtonDropdown from '@ui/components/ButtonDropdown';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { getTransportDashboardSummaryAsync, getTransportDashboardTrendAsync, getMapsKeyAsync } from '@application/features/pickupLocation/pickupLocationThunk';
import { FamilyFestGetTransportDashboardSummaryAsync, FamilyFestGetTransportDashboardTrendAsync } from '@application/features/familyFest/familyFestPickupLocationThunk';
import { TransportDashboardTrendRange } from '@application/features/pickupLocation/models/transport-dashboard';
import { EventTypeEnum } from '@application/enums/event-type-enum';
import { Icon } from '@iconify/react';
const TransportMap = ({ apiKey, locations }) => {
    const { t } = useTranslation();
    const mapRef = useRef(null);
    const [hoveredLocationId, setHoveredLocationId] = useState(null);
    const isZoomingRef = useRef(false);
    const mapLocations = useMemo(() => locations.filter((location) => !location.isClosed &&
        location.latitude !== null &&
        location.latitude !== undefined &&
        location.longitude !== null &&
        location.longitude !== undefined), [locations]);
    const defaultCenter = useMemo(() => mapLocations.length > 0
        ? {
            lat: Number(mapLocations[0].latitude),
            lng: Number(mapLocations[0].longitude)
        }
        : { lat: 52.1326, lng: 5.2913 }, [mapLocations]);
    const hoveredLocation = useMemo(() => mapLocations.find((location) => location.id === hoveredLocationId) ?? null, [mapLocations, hoveredLocationId]);
    const { isLoaded } = useJsApiLoader({
        id: 'transport-dashboard-google-map',
        googleMapsApiKey: apiKey
    });
    const fitMapToMarkers = useCallback(() => {
        const map = mapRef.current;
        if (!map || !window.google?.maps)
            return;
        if (mapLocations.length === 0)
            return;
        if (mapLocations.length === 1) {
            map.setCenter({
                lat: Number(mapLocations[0].latitude),
                lng: Number(mapLocations[0].longitude)
            });
            map.setZoom(12);
            return;
        }
        const bounds = new window.google.maps.LatLngBounds();
        mapLocations.forEach((location) => {
            bounds.extend({
                lat: Number(location.latitude),
                lng: Number(location.longitude)
            });
        });
        // Extra bottom padding so the southernmost pin stays above map UI / rounded clip.
        map.fitBounds(bounds, { top: 40, right: 40, bottom: 72, left: 40 });
    }, [mapLocations]);
    useEffect(() => {
        if (!isLoaded || !mapRef.current)
            return;
        const id = requestAnimationFrame(() => fitMapToMarkers());
        return () => cancelAnimationFrame(id);
    }, [isLoaded, fitMapToMarkers]);
    if (!isLoaded) {
        return (_jsx(Typography, { variant: 'body2', color: 'text.secondary', children: t('events.eventDetails.transportTab.loadingMap') }));
    }
    return (_jsxs(GoogleMap, { mapContainerStyle: { width: '100%', height: '100%' }, center: defaultCenter, zoom: 7, onLoad: (map) => {
            mapRef.current = map;
            requestAnimationFrame(() => fitMapToMarkers());
            map.addListener('zoom_changed', () => {
                isZoomingRef.current = true;
            });
            map.addListener('idle', () => {
                isZoomingRef.current = false;
            });
        }, options: {
            streetViewControl: false,
            fullscreenControl: true,
            mapTypeControl: false
        }, children: [mapLocations.map((location) => (_jsx(Marker, { position: {
                    lat: Number(location.latitude),
                    lng: Number(location.longitude)
                }, title: location.name, onMouseOver: () => setHoveredLocationId(location.id), onMouseOut: () => {
                    if (!isZoomingRef.current) {
                        setHoveredLocationId((current) => (current === location.id ? null : current));
                    }
                } }, location.id))), hoveredLocation && (_jsx(OverlayViewF, { position: {
                    lat: Number(hoveredLocation.latitude),
                    lng: Number(hoveredLocation.longitude)
                }, mapPaneName: OverlayView.FLOAT_PANE, children: _jsxs(Box, { sx: {
                        transform: 'translate(-50%, calc(-100% - 40px))',
                        pointerEvents: 'none',
                        bgcolor: 'white',
                        borderRadius: 1,
                        boxShadow: 3,
                        minWidth: 200,
                        p: 1.5
                    }, children: [_jsx(Typography, { variant: 'subtitle2', sx: { mb: 0.5 }, children: hoveredLocation.name }), _jsxs(Typography, { variant: 'body2', children: [t('events.eventDetails.transportTab.seats'), ": ", hoveredLocation.capacity ?? 0] }), _jsxs(Typography, { variant: 'body2', children: [t('events.eventDetails.transportTab.registered'), ": ", hoveredLocation.assigned ?? 0] }), hoveredLocation.supplierName && (_jsxs(Typography, { variant: 'body2', children: [t('events.eventDetails.transportTab.supplier'), ": ", hoveredLocation.supplierName] })), hoveredLocation.routes && hoveredLocation.routes.length > 0 && (_jsxs(Box, { sx: { mt: 1, borderTop: '1px solid', borderColor: 'divider', pt: 0.75 }, children: [_jsx(Typography, { variant: 'caption', sx: { fontWeight: 600, display: 'block', mb: 0.25 }, children: t('events.eventDetails.routeTab.routes') }), hoveredLocation.routes.map((route, idx) => (_jsxs(Box, { display: 'flex', justifyContent: 'space-between', gap: 2, children: [_jsx(Typography, { variant: 'caption', color: 'text.secondary', children: route.routeName }), _jsx(Typography, { variant: 'caption', sx: { fontWeight: 500 }, children: route.boardingCount })] }, idx)))] }))] }) }))] }));
};
const DashboardContent = ({ selectedLocationId }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const { eventId } = useParams();
    // Select the specific event to check its type
    const event = useAppSelector((state) => state.VDKEvent.VDKEventsForEventDetails);
    const isFamilyFest = event?.type === EventTypeEnum.Family;
    // Dashboard data (summary, trend, map markers) is scoped by the selected companies via the
    // auth token, so it must be refetched whenever the sidebar company selection changes.
    const selectedCompanyIds = useAppSelector((state) => state.company.selectedCompanyId);
    // Select dashboard data from the appropriate slice
    const standardDashboard = useAppSelector((state) => state.pickupLocation);
    const familyFestDashboard = useAppSelector((state) => state.familyFestPickupLocation);
    const mapsKey = useAppSelector((state) => state.pickupLocation.mapsKey);
    const transportDashboardSummary = isFamilyFest
        ? familyFestDashboard.transportDashboardSummary
        : standardDashboard.transportDashboardSummary;
    const transportDashboardTrend = isFamilyFest
        ? familyFestDashboard.transportDashboardTrend
        : standardDashboard.transportDashboardTrend;
    const [trendRange, setTrendRange] = useState(TransportDashboardTrendRange.ThisWeek);
    useEffect(() => {
        if (!eventId)
            return;
        if (isFamilyFest) {
            dispatch(FamilyFestGetTransportDashboardSummaryAsync({
                pickupLocationId: selectedLocationId
            }));
        }
        else {
            dispatch(getTransportDashboardSummaryAsync({
                eventId,
                pickupLocationId: selectedLocationId
            }));
        }
    }, [dispatch, eventId, selectedLocationId, isFamilyFest, selectedCompanyIds]);
    useEffect(() => {
        if (!eventId)
            return;
        if (isFamilyFest) {
            dispatch(FamilyFestGetTransportDashboardTrendAsync({
                pickupLocationId: selectedLocationId,
                trendRange: trendRange
            }));
        }
        else {
            dispatch(getTransportDashboardTrendAsync({
                eventId,
                pickupLocationId: selectedLocationId,
                trendRange
            }));
        }
    }, [dispatch, eventId, selectedLocationId, trendRange, isFamilyFest, selectedCompanyIds]);
    useEffect(() => {
        if (!mapsKey?.key) {
            dispatch(getMapsKeyAsync());
        }
    }, [dispatch, mapsKey?.key]);
    const barChartTitle = t('events.eventDetails.transportTab.TotalSeatsVsFilled');
    const donutTitle = t('events.eventDetails.transportTab.passengerTypeDistribution');
    const capacityLabel = t('events.eventDetails.transportTab.totalSeats');
    const registeredLabel = t('events.eventDetails.transportTab.filled');
    const registrationTrendTitle = t('events.eventDetails.transportTab.registrationTrend');
    const assignedVsUnassignedTitle = t('events.eventDetails.transportTab.assignedVsUnassigned');
    const summary = transportDashboardSummary;
    const trend = transportDashboardTrend;
    const totalAttendeesText = !summary || summary.totalAssigned + summary.totalUnassigned === 0
        ? '-'
        : String(summary.totalAssigned + summary.totalUnassigned);
    const seatsUtilizationText = !summary || summary.totalCapacity === 0
        ? '-'
        : `${summary.totalAssigned ?? 0}/${summary.totalCapacity}`;
    const overbookedText = !summary ||
        (isFamilyFest
            ? summary.totalRouteCount === 0
            : summary.overbookedLocationsCount === 0)
        ? '-'
        : String(isFamilyFest
            ? summary.totalRouteCount
            : summary.overbookedLocationsCount);
    const birthdaysText = !summary || summary.birthdays === 0 ? '-' : String(summary.birthdays);
    const trendOptions = useMemo(() => [
        {
            id: TransportDashboardTrendRange.ThisWeek,
            text: String(t('events.eventDetails.transportTab.thisWeek')),
            to: () => { },
            group: true
        },
        {
            id: TransportDashboardTrendRange.LastWeek,
            text: String(t('events.eventDetails.transportTab.lastWeek')),
            to: () => { },
            group: true
        },
        {
            id: TransportDashboardTrendRange.ThisMonth,
            text: String(t('events.eventDetails.transportTab.thisMonth')),
            to: () => { },
            group: true
        },
        {
            id: TransportDashboardTrendRange.LastMonth,
            text: String(t('events.eventDetails.transportTab.lastMonth')),
            to: () => { },
            group: true
        },
        {
            id: TransportDashboardTrendRange.Last3Months,
            text: String(t('events.eventDetails.transportTab.last3Months')),
            to: () => { },
            group: true
        },
        {
            id: TransportDashboardTrendRange.ThisYear,
            text: String(t('events.eventDetails.transportTab.thisYear')),
            to: () => { },
            group: true
        },
        {
            id: TransportDashboardTrendRange.LastYear,
            text: String(t('events.eventDetails.transportTab.lastYear')),
            to: () => { },
            group: true
        }
    ], [t]);
    const selectedTrendLabel = trendOptions.find((option) => option.id === trendRange)?.text ??
        String(t('events.eventDetails.transportTab.thisWeek'));
    const capacityVsRegisteredData = (summary?.locations ?? []).map((location) => ({
        label: location.name,
        capacity: location.capacity ?? 0,
        registered: location.assigned ?? 0
    }));
    const passengerTypeData = {
        labels: [
            String(t('events.eventDetails.transportTab.internal')),
            String(t('events.eventDetails.transportTab.external'))
        ],
        values: [summary?.internalAttendees ?? 0, summary?.externalAttendees ?? 0],
        colors: ['#3B82F6', '#F59E0B']
    };
    const assignedVsUnassignedData = {
        labels: [
            t('events.eventDetails.transportTab.assigned'),
            t('events.eventDetails.transportTab.unassigned')
        ],
        values: [summary?.totalAssigned ?? 0, summary?.totalUnassigned ?? 0],
        colors: ['#3B82F6', '#9CA3AF']
    };
    const registrationTrendData = (trend?.assignmentTrend ?? []).map((item) => ({
        label: item.label,
        value: item.count
    }));
    const trendMax = Math.max(...registrationTrendData.map((x) => x.value), 0);
    const yMax = trendMax <= 10 ? 10 : Math.ceil(trendMax * 1.2);
    const yStep = Math.max(1, Math.ceil(yMax / 5));
    const capacityChartMax = Math.max(...capacityVsRegisteredData.flatMap((d) => [d.capacity, d.registered]), 0);
    const capacityChartYMax = capacityChartMax <= 10 ? 10 : Math.ceil(capacityChartMax * 1.2);
    const capacityChartYStep = Math.max(1, Math.ceil(capacityChartYMax / 5));
    const MAX_COMPANIES_FOR_MAP = 20;
    const showMap = !isFamilyFest || (selectedCompanyIds?.length ?? 0) <= MAX_COMPANIES_FOR_MAP;
    return (_jsx(Box, { sx: { width: '100%', height: 'calc(100dvh - 220px)', overflowY: 'auto' }, children: _jsxs(Grid, { container: true, spacing: 2, sx: { width: '100%' }, children: [_jsx(Grid, { size: { xs: 12, lg: showMap ? 5 : 12 }, children: _jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { size: { xs: 12, sm: 6, md: showMap ? 6 : 3 }, children: _jsx(AvatarInfoCard, { header: t('events.eventDetails.transportTab.totalAttendees'), subheader: totalAttendeesText, labelIcon: _jsx(User, { variant: 'Outline', size: 20 }) }) }), _jsx(Grid, { size: { xs: 12, sm: 6, md: showMap ? 6 : 3 }, children: _jsx(AvatarInfoCard, { header: t('events.eventDetails.transportTab.seatsUtilization'), subheader: seatsUtilizationText, labelIcon: _jsx(Icon, { icon: 'hugeicons:chair-01', width: '20', height: '20' }) }) }), _jsx(Grid, { size: {
                                    xs: 12,
                                    sm: isFamilyFest ? 6 : 12,
                                    md: showMap ? (isFamilyFest ? 6 : 12) : isFamilyFest ? 3 : 6
                                }, children: _jsx(AvatarInfoCard, { header: isFamilyFest
                                        ? t('events.eventDetails.transportTab.totalRoute')
                                        : t('events.eventDetails.transportTab.overbookedAttendees'), subheader: overbookedText, labelIcon: _jsx(Warning2, { variant: 'Outline', size: 20 }) }) }), isFamilyFest && (_jsx(Grid, { size: { xs: 12, sm: 6, md: showMap ? 6 : 3 }, children: _jsx(AvatarInfoCard, { header: t('events.eventDetails.transportTab.birthdays'), subheader: birthdaysText, labelIcon: _jsx(Cake, { variant: 'Outline', size: 20 }) }) }))] }) }), showMap && (_jsx(Grid, { size: { xs: 12, lg: 7 }, children: _jsx(Box, { sx: {
                            height: '100%',
                            borderRadius: 4,
                            overflow: 'hidden',
                            bgcolor: theme.palette.grey[200],
                            border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }, children: mapsKey?.key ? (_jsx(TransportMap, { apiKey: mapsKey.key, locations: summary?.locations ?? [] })) : (_jsx(Typography, { variant: 'body2', color: 'text.secondary', children: t('events.eventDetails.transportTab.loadingMapKey') })) }) })), _jsx(Grid, { size: { xs: 12, md: isFamilyFest ? 12 : 8 }, children: _jsx(PeopleCard, { heading: barChartTitle, variant: 'grey', disableShadow: true, headerTypographyVariant: 'h6', height: '361px', children: capacityVsRegisteredData.length > 0 ? (_jsx(CapacityVsRegisteredBarChart, { data: capacityVsRegisteredData, capacityLabel: capacityLabel, registeredLabel: registeredLabel, yMin: 0, yMax: capacityChartYMax, yStep: capacityChartYStep, fillParent: true })) : (_jsx(Box, { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', children: _jsx(Typography, { variant: 'body2', color: 'text.secondary', children: t('events.eventDetails.transportTab.noDataAvailable') }) })) }) }), !isFamilyFest && (_jsx(Grid, { size: { xs: 12, md: 4 }, children: _jsx(PeopleCard, { heading: donutTitle, variant: 'grey', disableShadow: true, headerTypographyVariant: 'h6', height: '361px', children: passengerTypeData.values[0] + passengerTypeData.values[1] > 0 ? (_jsx(DonutChart, { fillParent: true, data: passengerTypeData, centerText: `${passengerTypeData.values[0] + passengerTypeData.values[1]}` })) : (_jsx(Box, { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', children: _jsx(Typography, { variant: 'body2', color: 'text.secondary', children: t('events.eventDetails.transportTab.noPassengerTypeData') }) })) }) })), _jsx(Grid, { size: { xs: 12, md: 8 }, children: _jsx(PeopleCard, { heading: registrationTrendTitle, variant: 'grey', disableShadow: true, headerTypographyVariant: 'h6', height: '361px', actionNode: _jsx(Box, { sx: { display: 'flex', justifyContent: 'flex-end', mb: 1 }, children: _jsx(ButtonDropdown, { label: selectedTrendLabel, keepOriginalOrder: true, variant: 'text', color: 'inherit', size: 'small', options: trendOptions, selectedItem: trendRange, onSelectionChange: (id) => setTrendRange(id) }) }), children: trendMax > 0 ? (_jsx(RegistrationTrendChart, { data: registrationTrendData, fillParent: true, yMin: 0, yMax: yMax, yStep: yStep })) : (_jsx(Box, { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', children: _jsx(Typography, { variant: 'body2', color: 'text.secondary', children: t('events.eventDetails.transportTab.noDataAvailable') }) })) }) }), _jsx(Grid, { size: { xs: 12, md: 4 }, children: _jsx(PeopleCard, { heading: assignedVsUnassignedTitle, variant: 'grey', disableShadow: true, headerTypographyVariant: 'h6', height: '361px', children: assignedVsUnassignedData.values[0] + assignedVsUnassignedData.values[1] > 0 ? (_jsx(DonutChart, { data: assignedVsUnassignedData, fillParent: true, centerText: String((summary?.totalAssigned ?? 0) + (summary?.totalUnassigned ?? 0)) })) : (_jsx(Box, { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', children: _jsx(Typography, { variant: 'body2', color: 'text.secondary', children: t('events.eventDetails.transportTab.noDataAvailable') }) })) }) })] }) }));
};
export default DashboardContent;
